const STALE_CHUNK_RECOVERY_KEY = 'speed.stale-chunk-recovery.attempted';
const DYNAMIC_IMPORT_ERROR = /^Failed to fetch dynamically imported module: (https?:\/\/\S+)$/;
const CHUNK_FILE = /\/chunk-[A-Z0-9]+\.js$/;

export interface StaleChunkRecoveryEnvironment {
  addUnhandledRejectionListener(listener: StaleChunkRejectionListener): void;
  origin: string;
  reload(): void;
  storage: Pick<Storage, 'getItem' | 'setItem'>;
}

export type StaleChunkRejectionListener = (event: {
  reason: unknown;
  preventDefault(): void;
}) => void;

export function installStaleChunkRecovery(
  environment: StaleChunkRecoveryEnvironment = browserEnvironment(),
): void {
  let reloadRequested = false;

  environment.addUnhandledRejectionListener((event) => {
    if (reloadRequested || !isSameOriginChunkImportError(event.reason, environment.origin)) {
      return;
    }

    try {
      if (environment.storage.getItem(STALE_CHUNK_RECOVERY_KEY) !== null) {
        return;
      }
      environment.storage.setItem(STALE_CHUNK_RECOVERY_KEY, '1');
    } catch {
      return;
    }

    reloadRequested = true;
    event.preventDefault();
    environment.reload();
  });
}

function browserEnvironment(): StaleChunkRecoveryEnvironment {
  return {
    addUnhandledRejectionListener(listener) {
      window.addEventListener('unhandledrejection', listener);
    },
    origin: window.location.origin,
    reload() {
      window.location.reload();
    },
    storage: window.sessionStorage,
  };
}

function isSameOriginChunkImportError(reason: unknown, origin: string): boolean {
  if (!(reason instanceof Error)) {
    return false;
  }

  const match = DYNAMIC_IMPORT_ERROR.exec(reason.message);
  if (!match) {
    return false;
  }

  try {
    const url = new URL(match[1]);
    return url.origin === origin && CHUNK_FILE.test(url.pathname);
  } catch {
    return false;
  }
}
